import { SortInfo } from '~/utils/jsonapi-query';
import { Money } from './money';

interface FixedAssetGroupBase {
  id: string;
  code: string;
  name: string;
}

export interface FixedAssetGroup extends FixedAssetGroupBase {
  species: FixedAssetSpecies[];
  created_at: string;
  updated_at: string;
}

type AssetType = 'TANGIBLE' | 'INTANGIBLE';
type AcquisitionType = 'NEW' | 'USED' | 'LEASING_BUYOUT';
type DepreciationType = 'DEPRECIABLE' | 'NON_DEPRECIABLE' | 'LOW_VALUE_FULLY_DEPRECIABLE';
type DeductibilityType =
  | 'MOTOR_VEHICLES_MIXED_USE_COMPANY'
  | 'MOTOR_VEHICLES_SALES_AGENTS'
  | 'MOTOR_VEHICLES_EMPLOYEE_USE'
  | 'MOTOR_VEHICLES_FRINGE_BENEFIT'
  | 'MOTORCYCLES_MIXED_USE_COMPANY'
  | 'MOTORCYCLES_SALES_AGENTS'
  | 'MOTORCYCLES_EMPLOYEE_USE'
  | 'MOTORCYCLES_FRINGE_BENEFIT'
  | 'MOPEDS_MIXED_USE_COMPANY'
  | 'MOPEDS_SALES_AGENTS'
  | 'MOPEDS_EMPLOYEE_USE'
  | 'MOPEDS_FRINGE_BENEFIT'
  | 'TELEPHONY'
  | 'FULLY_DEDUCTIBLE'
  | 'OTHER';
type FixedAssetStatus = 'IN_USE' | 'NOT_IN_USE';

export interface FixedAssetSpeciesCategory {
  id: string;
  speciesId: string;
  categoryId: string;
  rate: number;
  species?: FixedAssetSpecies;
  assetAccountId?: string | null;
  depreciationAccountId?: string | null;
  accumulatedDepreciationAccountId?: string | null;
  // Optional: the `category` relationship is only present when the catalog response hydrated it
  // (JSON:API `include=…speciesCategories.category`). An incomplete `included` leaves it undefined,
  // so every read must guard against it — that absence is what silently blanks the category.
  category?: {
    code: string;
    name: string;
    legalReference: string;
    assetType: AssetType;
    depreciationType: DepreciationType;
  };
}

interface FixedAssetSpecies {
  id: string;
  code: string;
  name: string;
  groupId: string;
  group?: FixedAssetGroupBase;
  speciesCategories?: FixedAssetSpeciesCategory[];
}

interface FixedAssetSourceDocument {
  id: string;
  number: string | null;
  counterpartName: string | null;
}

interface FixedAssetVATRegistration {
  id: string;
  number: number;
  documents?: FixedAssetSourceDocument[];
}

interface FixedAssetVATEntry {
  id: string;
  vatRegistration?: FixedAssetVATRegistration | null;
}

export interface CompanyFixedAssetSpecies {
  id: string;
  companyId: string;
  speciesId: string;
  species: FixedAssetSpecies;
}

/**
 * @public
 */
export interface FixedAsset {
  id: string;
  description: string;
  speciesCategory?: FixedAssetSpeciesCategory;
  assetType: AssetType;
  depreciationType: DepreciationType;
  acquisitionDate: string;
  serviceStartDate: string;
  acquisitionType: AcquisitionType;
  sourceTaxableAmount: Money;
  vatPaidTotal: Money;
  vatDeductibleAmount: Money;
  vatNonDeductibleAmount: Money;
  initialBookValue: Money;
  initialTaxValue: Money;
  /**
   * Gross book value the depreciation plan runs on: equals {@link initialBookValue} at
   * acquisition and only moves through capitalizable increments. Optional because the backend
   * only started serializing it with the depreciation plans.
   */
  currentBookValue?: Money | null;
  /** Deductible base of the plan, derived as `min(currentBookValue, cap) × rate`. */
  currentTaxValue?: Money | null;
  /**
   * Funds brought in from a previous software, declared at registration. The plan does not cover
   * the exercises they account for, so they are the only source of fund/residual for those years.
   */
  carriedOverBookDepreciation?: Money | null;
  carriedOverTaxDepreciation?: Money | null;
  /** Last exercise the carried over funds account for. */
  carriedOverThroughYear?: number | null;
  bookDepreciationRate: string;
  taxDepreciationRate: string;
  referenceDepreciationRate: string;
  taxDeductibilityType: DeductibilityType;
  taxDeductibilityRate: string;
  taxDeductibilityCap: Money | null;
  status?: FixedAssetStatus;
  disposalDate?: string | null;
  disposalAmount?: Money | null;
  notes?: string | null;
  vatEntry?: FixedAssetVATEntry | null;
}

type FixedAssetDepreciationPlanEntryStatus = 'REGISTERED' | 'TO_REGISTER';

/**
 * One yearly row of a fixed asset depreciation plan, as serialized by the backend
 * (`book_*` fields are the civil plan, `tax_*` fields the deductible one).
 *
 * @public
 */
export interface FixedAssetDepreciationPlanEntry {
  id: string;
  year: number;
  date: string;
  bookDepreciationAmount: Money;
  bookDepreciationFund: Money;
  bookResidualAmount: Money;
  taxDepreciationAmount: Money;
  taxDepreciationFund: Money;
  taxResidualAmount: Money;
  status: FixedAssetDepreciationPlanEntryStatus;
}

export interface FixedAssetsQueryFilters {
  search?: string;
  categoryId?: string | null;
  acquisitionDate?: [string | null, string | null];
}

export type FixedAssetsQuerySorting = 'id' | 'acquisition_date' | 'service_start_date' | 'created_at';

/** @public */
export const FixedAssetQueryKeys = {
  all: ['FixedAssets'],
  single: (id: FixedAsset['id']) => ['FixedAssets', id],
  list: (companyId: string, filters: FixedAssetsQueryFilters, sorting: SortInfo<FixedAssetsQuerySorting> | null) => [
    'FixedAssets',
    { companyId },
    filters,
    sorting,
  ],
  movements: (assetId: FixedAsset['id']) => ['FixedAssets', assetId, 'Movements'],
  depreciationPlan: (assetId: FixedAsset['id']) => ['FixedAssets', assetId, 'DepreciationPlan'],
  byDocument: (documentId: string) => ['FixedAssets', 'ByDocument', { documentId }],
  inUseSpecies: (companyId: string) => ['FixedAssets', 'InUseSpecies', { companyId }],
};

export const FixedAssetsSpeciesQueryKeys = {
  byCompany: (companyId: string) => ['fixed-assets-species', companyId],
};

export const FixedAssetGroupQueryKeys = {
  all: ['fixed-asset-groups'],
};

/** @public */
export type FixedAssetDraft = Partial<{
  description: string;
  categoryName: string;
  speciesName: string;
  groupName: string;
  speciesCategoryId: string;
  assetType: AssetType;
  depreciationType: DepreciationType;
  referenceDepreciationRate: number;
  acquisitionDate: string;
  serviceStartDate: string;
  originalBookValue: number;
  acquisitionType: AcquisitionType;
  bookDepreciationRate: number;
  taxDepreciationRate: number;
  taxDeductibilityType: string;
  taxDeductibilityRate: number;
  taxDeductibilityCap: number | null;
  notes: string | null;
}>;

export interface FixedAssetUpdatePayload {
  description?: string;
  serviceStartDate?: string;
  acquisitionType?: AcquisitionType;
  initialBookValue?: Money;
  bookDepreciationRate?: string;
  taxDepreciationRate?: string;
  taxDeductibilityType?: string;
  taxDeductibilityRate?: string;
  taxDeductibilityCap?: Money | null;
  notes?: string | null;
  status?: FixedAssetStatus;
}

/**
 * Backend-shaped fixed asset, ready to be serialized as a `fixed-asset` JSON:API resource
 * nested under a `vat-entry`. Field names match the Ecto changeset (camelCased).
 */
export interface FixedAssetPayload {
  speciesCategoryId?: string;
  description?: string;
  acquisitionDate?: string;
  serviceStartDate?: string;
  sourceTaxableAmount: Money;
  vatPaidTotal: Money;
  vatDeductibleAmount: Money;
  vatNonDeductibleAmount: Money;
  initialBookValue: Money;
  acquisitionType?: AcquisitionType;
  bookDepreciationRate?: string;
  taxDepreciationRate?: string;
  taxDeductibilityType?: string;
  taxDeductibilityRate?: string;
  taxDeductibilityCap: Money | null;
  status: 'IN_USE';
  depreciationType?: DepreciationType;
  assetType?: AssetType;
  referenceDepreciationRate?: string;
  notes?: string | null;
}

/**
 * Deserialized `fixed-asset` resource as returned nested under a `vat-entry` (see backend
 * `FixedAssetsJSON`). Decimals arrive as strings; money fields as {@link Money} objects.
 */
export interface FixedAssetResource {
  id: string;
  description?: string | null;
  acquisitionDate?: string | null;
  serviceStartDate?: string | null;
  initialBookValue?: Money | null;
  acquisitionType?: AcquisitionType | null;
  bookDepreciationRate?: string | number | null;
  taxDepreciationRate?: string | number | null;
  taxDeductibilityType?: string | null;
  taxDeductibilityRate?: string | number | null;
  taxDeductibilityCap?: Money | null;
  assetType?: AssetType | null;
  depreciationType?: DepreciationType | null;
  referenceDepreciationRate?: string | number | null;
  notes?: string | null;
  speciesCategory?: { id: string } | null;
}

export interface VATEntryAmounts {
  /** Total imponibile of the entry; the sum of the assets' sourceTaxableAmount must equal this. */
  taxableAmount: Money;
  /** Total VAT of the entry; the sum of the assets' vatPaidTotal must equal this. */
  taxAmount: Money;
  /** VAT deductibility of the entry's VAT code, as a 0-100 percentage. Defaults to 100 when missing. */
  vatDeductibility?: number | null;
}
